import React from 'react';
import clsx from 'clsx';
import { Loader2, RotateCcw } from 'lucide-react';

/**
 * Floating refresh button + transient toast for the admin dashboard.
 * The actual refresh handlers are registered per-tab by descendants via
 * `registerRefresh(tabId, handler)`; clicking the FAB triggers the handler
 * for the currently active tab.
 *
 * Keyboard: Alt+R triggers the same refresh.
 */
export function RefreshFab({ refreshing, refreshStatus, onRefresh, toast }) {
    return (
        <>
            <div className="pointer-events-none fixed left-4" style={{ bottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))' }}>
                <div className="relative">
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={refreshing}
                        aria-disabled={refreshing ? 'true' : 'false'}
                        aria-label="רענן"
                        className={clsx(
                            'group pointer-events-auto w-13 h-13 rounded-pill inline-flex items-center justify-center',
                            'bg-brand-500 text-ink-inverse shadow-elevated',
                            'transition-[background-color,box-shadow,transform] duration-200 ease-smooth',
                            'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-400/40',
                            refreshing ? 'opacity-80' : 'hover:bg-brand-600 hover:shadow-xl active:scale-95',
                        )}
                    >
                        <div
                            className="absolute bottom-full mb-2 left-1 px-2 py-1 rounded-md bg-ink-strong text-ink-inverse text-2xs font-medium opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity whitespace-nowrap shadow-elevated"
                            role="tooltip"
                        >
                            רענן (Alt+R)
                        </div>
                        {refreshing ? <Loader2 className="w-6 h-6 animate-spin" /> : <RotateCcw className="w-6 h-6" />}
                        <span className="sr-only" aria-live="polite">{refreshStatus}</span>
                    </button>
                </div>
            </div>

            {toast && (
                <div
                    aria-live="polite"
                    className="fixed right-4 z-toast rounded-lg shadow-elevated px-4 py-3 text-sm font-medium animate-fade-in"
                    style={{ bottom: 'calc(9.5rem + env(safe-area-inset-bottom, 0px))' }}
                >
                    <div
                        className={clsx(
                            'flex items-center gap-2 rounded-lg px-3 py-2 border',
                            toast.type === 'error'
                                ? 'bg-danger-50 text-danger-700 border-danger-100'
                                : 'bg-success-50 text-success-700 border-success-100',
                        )}
                    >
                        {toast.message}
                    </div>
                </div>
            )}
        </>
    );
}

export default RefreshFab;
